import {FC, useEffect, useState} from 'react';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Textarea,
  Text,
  Title,
} from '@mantine/core';
import {TrackedSerialNumbersService} from '../api/generated/TrackedSerialNumbersService';
import {RigsService} from '../api/generated/RigsService';
import {TrackedSerialNumberListingDto} from '../api/generated/index.defs';
import {useOptions} from '../hooks/use-options';
import { Select } from "../components/inputs/combobox/select.tsx";
import { Nullish } from "../types";

// Service status options for user-managed status
const ServiceStatusOptions = [
  {value: 'Active', label: 'Active'},
  {value: 'Inactive', label: 'Inactive'},
];

// Status values that are job-driven and cannot be changed by user
const JobDrivenStatuses = ['Scrapped', 'In Repair'];

type TrackedSnEditModalProps = {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: TrackedSerialNumberListingDto | undefined;
  partnerSiteId: number;
};

export const TrackedSnEditModal: FC<TrackedSnEditModalProps> = ({
  opened,
  onClose,
  onSuccess,
  item,
  partnerSiteId,
}) => {
  // Form state
  const [selectedRigId, setSelectedRigId] = useState<Nullish<string>>(null);
  const [selectedServiceStatus, setSelectedServiceStatus] =
    useState<Nullish<string>>(null);
  const [inactiveReason, setInactiveReason] = useState<Nullish<string>>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch rig options for the dropdown
  const rigOptionsResult = useOptions(
    () => RigsService.getOptions({partnerSiteId}),
    [partnerSiteId]
  );
  // Map options to Mantine Select format (value must be string)
  const rigOptions = (rigOptionsResult.value ?? []).map((opt) => ({
    value: String(opt.value),
    label: opt.label,
  }));

  // Check if status is job-driven (cannot be changed)
  const isStatusJobDriven = item?.serviceStatus
    ? JobDrivenStatuses.includes(item.serviceStatus)
    : false;

  // Reset form state when modal opens with new item
  useEffect(() => {
    if (opened && item) {
      setSelectedRigId(item.rigId?.toString() ?? null);
      // Use the stored ServiceStatus from the entity, not the calculated status
      // For now, we map from the calculated status (this will be refined after API regeneration)
      setSelectedServiceStatus(
        isStatusJobDriven ? null : (item.serviceStatus === 'Active' ? 'Active' : item.serviceStatus === 'Inactive' ? 'Inactive' : 'Active')
      );
      setInactiveReason(item.inactiveReason ?? null);
      setError(null);
    }
  }, [opened, item, isStatusJobDriven]);

  const handleSave = async () => {
    if (!item) return;

    setSaving(true);
    setError(null);

    try {
      // Check if rig changed
      const rigChanged = item.rigId?.toString() !== selectedRigId;
      const newRigId = selectedRigId ? parseInt(selectedRigId, 10) : null;

      // Check if status or inactive-reason changed (only if not job-driven)
      const currentStatus =
        item.serviceStatus === 'Inactive' ? 'Inactive' : 'Active';
      const statusChanged = !isStatusJobDriven &&
        selectedServiceStatus !== null &&
        selectedServiceStatus !== currentStatus;
      const reasonChanged = !isStatusJobDriven &&
        selectedServiceStatus === 'Inactive' &&
        (inactiveReason ?? '') !== (item.inactiveReason ?? '');

      // A reason is required whenever the serial number is Inactive
      if (!isStatusJobDriven && selectedServiceStatus === 'Inactive' && !inactiveReason?.trim()) {
        setError('A reason is required when setting a serial number to Inactive');
        setSaving(false);
        return;
      }

      // Assign to rig if changed
      if (rigChanged) {
        const assignResponse = await TrackedSerialNumbersService.assignToRig({
          id: item.id,
          body: {rigId: newRigId ?? undefined},
        });
        if (assignResponse.hasErrors) {
          setError(assignResponse.errors?.[0]?.errorMessage || 'Failed to assign rig');
          setSaving(false);
          return;
        }
      }

      // Update service status / inactive reason if changed
      if ((statusChanged || reasonChanged) && selectedServiceStatus) {
        const updateResponse = await TrackedSerialNumbersService.update({
          id: item.id,
          body: {
            notes: item.notes ?? undefined,
            serviceStatus: selectedServiceStatus,
            inactiveReason:
              selectedServiceStatus === 'Inactive' ? inactiveReason ?? undefined : undefined,
          },
        });
        if (updateResponse.hasErrors) {
          setError(updateResponse.errors?.[0]?.errorMessage || 'Failed to update service status');
          setSaving(false);
          return;
        }
      }

      onSuccess();
    } catch (e) {
      setError('An unexpected error occurred');
      // eslint-disable-next-line no-restricted-syntax
      console.error('Error saving tracked serial number:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Title order={4}>Edit Serial Number</Title>
          {item?.serialNumber && (
            <Badge color="gray" variant="light">
              S/N: {item.serialNumber}
            </Badge>
          )}
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {!item ? (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        ) : (
          <>
            {/* Rig Assignment */}
            <Select
              label="Rig"
              placeholder="Select a rig (optional)"
              data={rigOptions}
              value={selectedRigId}
              onChange={setSelectedRigId}
              clearable
              searchable
              disabled={saving}
            />

            {/* Service Status */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Service Status
              </Text>

              {isStatusJobDriven ? (
                <Group gap="xs">
                  <Badge
                    color={item.serviceStatusColor === "red" ? "red" : "yellow"}
                    variant="filled"
                    size="lg"
                  >
                    {item.serviceStatus}
                  </Badge>
                  <Text size="sm" c="dimmed">
                    (Job-driven status - cannot be changed)
                  </Text>
                </Group>
              ) : (
                <Select
                  placeholder="Select service status"
                  data={ServiceStatusOptions}
                  value={selectedServiceStatus}
                  onChange={setSelectedServiceStatus}
                  disabled={saving}
                />
              )}

              {!isStatusJobDriven && selectedServiceStatus === 'Inactive' && (
                <Textarea
                  label="Inactive Reason"
                  placeholder="Why is this serial number being marked inactive?"
                  value={inactiveReason ?? ''}
                  onChange={(event) => setInactiveReason(event.currentTarget.value)}
                  disabled={saving}
                  withAsterisk
                  autosize
                  minRows={2}
                  maxLength={1000}
                />
              )}
            </Stack>

            {/* Current Status Info */}
            {item.serviceStatus && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Current Status:{" "}
                  <Badge
                    color={item.serviceStatusColor || "gray"}
                    variant="filled"
                    size="sm"
                  >
                    {item.serviceStatus}
                  </Badge>
                </Text>
              </Stack>
            )}

            {/* Error message */}
            {error && (
              <Text c="red" size="sm">
                {error}
              </Text>
            )}

            {/* Actions */}
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} loading={saving}>
                Save Changes
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};